import {ofType} from 'redux-observable';
import {of} from 'rxjs';
import {mergeMap, map, catchError, flatMap, delay} from 'rxjs/operators';
import i18next from 'i18next';
import {destroy} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import * as PeoplePickerModalActions from 'web-app/react/modals-container/people-picker-modal/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {track} from 'web-app/util/famlytics/index';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import * as ChildContactActions from 'web-app/react/entities/contacts/actions';

import * as Actions from './actions';
import * as API from './api';
import * as Constants from './constants';
import * as Selectors from './selectors';

const updateChildPickup = (action$, state$) =>
    action$.pipe(
        ofType(Actions.updateCheckin.type),
        mergeMap(({payload}: ReturnType<typeof Actions.updateCheckin.action>) => {
            const {pickupTime, relationId, goHomeWithChildId} = payload.checkin;
            const canCheckIn = Selectors.getCanCheckIn(state$.value);
            const {childId} = Selectors.childSignIn(state$.value);
            const rolesIds = AccountSelectors.getRoles2(state$.value).map(role => role.targetId);
            const checkingInOutPersonType = rolesIds.includes(childId) ? 'parent' : 'employee';

            if (!canCheckIn) {
                return [
                    GlobalEventActions.updateError.action(i18next.t('checkin.selectTimeAndPickup')),
                    Actions.updateCheckinFailed.action(new Error('Pickup person and time are mandatory')),
                ];
            }

            return API.signInObservable(childId, pickupTime, relationId, goHomeWithChildId).pipe(
                flatMap(response => {
                    track(TrackingEvents.SIGN_IN_OUT_UPDATE_CHILD_PICK_UP, {
                        openedFrom: 'Child profile',
                        checkingInOutPersonType,
                        pickupTimeSelected: hasValue(pickupTime),
                        pickupPersonSelected: hasValue(response.pickupRelationId),
                    });

                    return [
                        Actions.updateCheckinSuccess.action(response),
                        GlobalEventActions.updateSuccess.action(i18next.t('checkin.expectedPickupUpdated')),
                        PeoplePickerModalActions.hide.action(),
                        Actions.hide.action(),
                    ];
                }),
                catchError(e => of(Actions.updateCheckinFailed.action(e))),
            );
        }),
    );

const clearFormOnModalClosed = action$ =>
    action$.pipe(
        ofType(Actions.hide.type),
        map(() => {
            return destroy(Constants.FormName);
        }),
    );

const fetchExtendedChildCheckin = action$ =>
    action$.pipe(
        ofType(Actions.fetchExtendedCheckin.type),
        mergeMap(({payload}: ReturnType<typeof Actions.fetchExtendedCheckin.action>) => {
            return API.fetchCheckinAndRelations(payload.childId).pipe(
                map(response => Actions.fetchExtendedCheckinSuccess.action(response)),
                catchError(e => of(Actions.fetchExtendedCheckinFailed.action(e))),
            );
        }),
    );

const fetchExtendedChildCheckinAfterAddContact = action$ =>
    action$.pipe(
        ofType(ChildContactActions.createChildContactSuccess.type),
        delay(250),
        mergeMap(({payload}: ReturnType<typeof Actions.fetchExtendedCheckin.action>) => {
            return API.fetchCheckinAndRelations(payload.childId).pipe(
                map(response => Actions.fetchExtendedCheckinSuccess.action(response)),
                catchError(e => of(Actions.fetchExtendedCheckinFailed.action(e))),
            );
        }),
    );

const selectGoHoWithChild = action$ =>
    action$.pipe(
        ofType(Actions.selectGoHomeWithChildId.type),
        map(() => {
            return PeoplePickerModalActions.hide.action();
        }),
    );

export default combineEpics(
    updateChildPickup,
    fetchExtendedChildCheckin,
    fetchExtendedChildCheckinAfterAddContact,
    selectGoHoWithChild,
    clearFormOnModalClosed,
);
